/* eslint-disable import/no-extraneous-dependencies */
import { Box, createTheme, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { ReactComponent as Hero } from "assets/background/hero.svg";
import gif from "assets/background/howitwrokssection.gif";
import theme from "assets/theme";
import Footer from "components/Footer/Footer";
import Navbar from "components/Navbar/Navbar";
import { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Route, Routes, useLocation } from "react-router-dom";
import routes from "routes/routes";
import { getSeoMetadata, getStructuredData } from "seo/seoConfig";

function App() {
  const location = useLocation();
  const isMobileOrTablet = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });

    // Track page views
    if (window.gtag) {
      window.gtag("config", "G-CSR28EDCD2", {
        page_path: location.pathname,
      });
    }
  }, [location.pathname]);

  const getRoutes = (allRoutes, parentRoute = "") =>
    allRoutes.flatMap((route) => {
      const { route: currentRoute, layout, component } = route;
      const fullRoute = parentRoute + currentRoute;
      const routePath = layout + fullRoute;

      const routeComponent = component && (
        <Route exact path={routePath} element={component} key={route.key} />
      );

      return routeComponent;
    });

  const appTheme = createTheme(theme);

  const showHero = ["/", "/about"].includes(location.pathname);
  const heroBackground = location.pathname === "/about" ? "#000" : "#FFF";
  const isBlogPage = location.pathname.includes("/blog/");

  // Get SEO metadata
  const { title, description, keywords, schemaType } = getSeoMetadata(location.pathname);
  const currentUrl = `https://newcontent.xane.ai/${location.pathname}`;
  const structuredData = getStructuredData(schemaType, title, description, currentUrl);

  return (
    <HelmetProvider>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <Helmet>
          {/* Primary Meta Tags */}
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta name="keywords" content={keywords} />
          <meta name="author" content="Xane AI" />
          <meta name="robots" content="index, follow" />
          <meta name="geo.region" content="IN-HR" />
          <meta name="geo.placename" content="Gurgaon" />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content="https://newcontent.xane.ai//og-image.jpg" />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={currentUrl} />
          <meta property="twitter:title" content={title} />
          <meta property="twitter:description" content={description} />
          <meta property="twitter:image" content="https://newcontent.xane.ai//og-image.jpg" />

          {/* Canonical */}
          <link rel="canonical" href={currentUrl} />

          {/* Structured Data */}
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            position: "relative",
            gap: "36px",
          }}
        >
          {/* Background only for / and /about */}
          {showHero && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: { xs: "895px", sm: "665px", md: "940px" },
                zIndex: -1,
                background: heroBackground,
                overflow: "hidden",
              }}
            >
              {!isMobileOrTablet && (
                <Hero
                  style={{
                    width: "100%",
                    height: location.pathname === "/about" ? "145%" : "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>
          )}
          <Navbar />
          {/* Special layout for /works */}
          {location.pathname === "/works" && (
            <Box
              sx={{
                height: "100%",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {!isMobileOrTablet && (
                <Hero
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    zIndex: 1,
                  }}
                />
              )}
              <Box
                sx={{
                  height: { xs: "240px", sm: "420px", md: "500px" },
                  width: "100%",
                  backgroundImage: `url(${gif})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  position: "relative",
                  zIndex: -2,
                }}
              />
            </Box>
          )}
          {/* Background for /blog/:id */}
          {isBlogPage && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: -1,
                width: "100%",
                height: { xs: "410px", sm: "657px", md: "657px" },
                background: "linear-gradient(270deg, #AB6AAC 18.76%, #7D3081 80.43%)",
              }}
            />
          )}

          <Box sx={{ flex: 1 }}>
            <Routes>{getRoutes(routes)}</Routes>
          </Box>
          <Footer />
        </Box>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
